import { useQuery } from "@tanstack/react-query";
import { Copy, Mail, MessageSquare, Share2, UserPlus } from "lucide-react";
import { type ReactNode, useState } from "react";
import { toast } from "sonner";
import { QrCode } from "@/components/qr-code";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/overlay";
import { Card, CardHeader, Spinner } from "@/components/ui/surface";
import { api } from "@/lib/api";
import { keys } from "@/lib/queries";

const MESSAGE = "I'm using GridAction to run football grids with friends. It's free, join me:";

const referralQuery = {
  queryKey: keys.referral,
  queryFn: () => api.get<{ code: string; url: string; joined: number }>("/me/referral"),
};

function InviteBody() {
  const { data } = useQuery(referralQuery);
  if (!data) {
    return (
      <div className="grid place-items-center py-8">
        <Spinner />
      </div>
    );
  }
  const canShare = typeof navigator.share === "function";
  const share = async () => {
    await navigator
      .share({ title: "Join me on GridAction", text: MESSAGE, url: data.url })
      .catch(() => undefined);
  };
  const copy = async () => {
    await navigator.clipboard.writeText(data.url);
    toast.success("Link copied");
  };
  const body = encodeURIComponent(`${MESSAGE} ${data.url}`);
  return (
    <div className="space-y-4">
      {canShare ? (
        <Button size="lg" className="w-full" onClick={() => void share()}>
          <Share2 className="size-4" aria-hidden /> Share with friends
        </Button>
      ) : null}
      <div className="grid grid-cols-3 gap-2">
        <Button variant="outline" onClick={() => void copy()}>
          <Copy className="size-4" aria-hidden /> Copy
        </Button>
        <Button asChild variant="outline">
          <a href={`sms:?&body=${body}`}>
            <MessageSquare className="size-4" aria-hidden /> Text
          </a>
        </Button>
        <Button asChild variant="outline">
          <a href={`mailto:?subject=${encodeURIComponent("Join me on GridAction")}&body=${body}`}>
            <Mail className="size-4" aria-hidden /> Email
          </a>
        </Button>
      </div>
      <p className="break-all rounded-xl bg-surface-2 px-3 py-2 text-center text-sm">{data.url}</p>
      <div className="flex justify-center">
        <QrCode value={data.url} size={150} label="QR code for your invite link" />
      </div>
      <p className="text-center text-sm text-muted">
        {data.joined === 0
          ? "Nobody has joined with your link yet."
          : `${data.joined} ${data.joined === 1 ? "friend has" : "friends have"} joined with your link.`}
      </p>
    </div>
  );
}

/** Opens the invite sheet from any trigger. */
export function InviteDialog({
  trigger,
  open: controlledOpen,
  onOpenChange,
}: {
  /** What opens the sheet. Leave it out to open it from outside with `open` and `onOpenChange`. */
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [ownOpen, setOwnOpen] = useState(false);
  const open = controlledOpen ?? ownOpen;
  const setOpen = (next: boolean) => {
    setOwnOpen(next);
    onOpenChange?.(next);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent
        title="Invite your friends to play"
        description="GridAction is free. Send your link, and we'll count everyone who joins with it."
      >
        {open ? <InviteBody /> : null}
      </DialogContent>
    </Dialog>
  );
}

/** A card for the dashboard and Settings. */
export function InviteCard() {
  return (
    <Card>
      <CardHeader
        title="Invite your friends to play"
        description="It's free. Share your link and see how many friends join."
      />
      <InviteDialog
        trigger={
          <Button>
            <UserPlus className="size-4" aria-hidden /> Invite friends
          </Button>
        }
      />
    </Card>
  );
}
